import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { Button } from '@/shared/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  buttonClassName?: string;
}

export const SearchableSelect = React.forwardRef<
  HTMLButtonElement,
  SearchableSelectProps
>(
  (
    {
      options = [],
      value,
      onValueChange,
      placeholder = 'Select option...',
      searchPlaceholder = 'Search...',
      emptyMessage = 'No options found.',
      className,
      disabled = false,
      buttonClassName,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');

    const filteredOptions = React.useMemo(
      () =>
        options
          .filter((option) =>
            option.label.toLowerCase().includes(searchValue.toLowerCase())
          )
          .sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true })
          ),
      [options, searchValue]
    );

    const selectedOption = React.useMemo(
      () => options.find((option) => option.value === value),
      [options, value]
    );

    const handleSelect = React.useCallback(
      (selectedValue: string) => {
        if (onValueChange) {
          onValueChange(selectedValue === value ? '' : selectedValue);
        }
        setOpen(false);
        setSearchValue('');
      },
      [onValueChange, value]
    );

    const handleOpenChange = React.useCallback((newOpen: boolean) => {
      setOpen(newOpen);
      if (!newOpen) {
        setSearchValue('');
      }
    }, []);

    return (
      <div className={cn('w-full', className)}>
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                'w-full justify-between border-border bg-card font-normal shadow-none hover:border-line-2 hover:bg-accent hover:text-foreground',
                !selectedOption && 'text-muted-foreground',
                buttonClassName
              )}
            >
              <span className="truncate">
                {selectedOption ? selectedOption.label : placeholder}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.9} />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="flex max-h-[min(400px,70vh)] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden p-0"
            align="start"
            onWheel={(e) => {
              e.stopPropagation();
            }}
          >
            <Command shouldFilter={false} className="flex flex-col overflow-hidden max-h-[min(400px,70vh)]">
              <CommandInput
                placeholder={searchPlaceholder}
                value={searchValue}
                onValueChange={setSearchValue}
                className="flex-shrink-0"
              />
              <CommandList className="flex-1 min-h-0 overflow-y-auto max-h-[300px]">
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {filteredOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => handleSelect(option.value)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          option.value === value ? 'text-primary opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="truncate">{option.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);

SearchableSelect.displayName = 'SearchableSelect';
